import { useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { addEdge, ConnectionLineType, getNodesBounds, MarkerType, useEdgesState, useNodesState, type Connection, type Node, type ReactFlowInstance, type Viewport } from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Box, Button, Collapse, Divider,Flex, FormControl, FormErrorMessage, FormLabel, HStack, IconButton, Input,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Tab, TabList, Tabs,
  Table, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast, VStack,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon, DragHandleIcon, } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { BomFlowEdge, BomFlowNode, BomFlowNodeData, BomMaterial } from '../../contexts/types';
import { buildBomExportAoa, computeLevels, detectCycle, downloadBomExcel, findProcessCodeConflicts } from './BomCreateModal.logic';
import { BomCreateFlowNode, BOM_NODE_WIDTH, BOM_NODE_HEIGHT } from './BomCreateFlowNode';
import { BomCreateGroupNode } from './BomCreateGroupNode';
import { bomSampleNodes, bomSampleEdges } from './BomCreateModal.sampleData';
import HelpIcon from '../InvestmentScreening/HelpIcon';

/** 一時保存ファイルの画面識別子（読み込み時の画面一致チェックに使用） */
const SCREEN_NAME = 'ProcessCreate';

/**
 * ReactFlowのNode type識別子。nodeTypes定義・Node生成の両方から参照し、文字列の重複・タイプミスを防ぐ。
 * PROCESS: 部品/工程を表すNode（BomCreateFlowNode）。GROUP: 整列時にのみ動的生成される兄弟ノード枠（BomCreateGroupNode）。
 */
const BOM_NODE_TYPE = {
  PROCESS: 'bomProcess',
  GROUP: 'bomGroup',
} as const;

/** ReactFlowの推奨に従い、nodeTypesはコンポーネント外の固定オブジェクトとして定義する（毎レンダーで再生成させない） */
const nodeTypes = { [BOM_NODE_TYPE.PROCESS]: BomCreateFlowNode, [BOM_NODE_TYPE.GROUP]: BomCreateGroupNode };

interface BomCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** アコーディオン内のセクション */
const HELP_SECTIONS = ['step0', 'stepSample', 'step1', 'step2', 'step3', 'step4', 'step5', 'step6and7', 'step8', 'step9'] as const;
/** 工程登録上限 */
const MAX_NODES = 200;
/** 材料登録上限 */
const MAX_MATERIALS = 30;
/** 新規ノード配置の基準座標（フォールバック用） */
const BASE_POSITION_FALLBACK = { x: 40, y: 40 };

/**
 * BOM（部品構成表）作成モーダル。
 * ReactFlow上でノード（工程）と矢印（親子関係）をGUIで配置・接続してもらい、
 * 矢印の向きからLevel（何次部品か）を自動算出、最終的にExcel(xlsx)としてダウンロードする。
 * 新規作成専用（既存BOMの編集は対象外）。
 */
const BomCreateModal: React.FC<BomCreateModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const bl = 'features.InvestmentScreening.BomCreateModal';

  // ---------------------------------------------------------------------------------------------------------------
  // Hooks宣言（State・Ref・Disclosure・派生値）
  // ---------------------------------------------------------------------------------------------------------------
  // ---useState---

  /** 「作成」タブ／「サンプル」タブの切り替え。モーダルを開いたときは常に「作成」がデフォルト */
  type BomModalTab = 'create' | 'sample';
  const [activeTab, setActiveTab] = useState<BomModalTab>('create');
  /**作成・サンプル画面の描画情報 */
  const [createViewport, setCreateViewport] = useState<Viewport | undefined>(undefined);
  const [sampleViewport, setSampleViewport] = useState<Viewport | undefined>(undefined);
  /** Nodeの設定 */
  const [nodes, setNodes, onNodesChange] = useNodesState<BomFlowNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<BomFlowEdge>([]);
  /** 作成・サンプル画面での選択状態 */
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedSampleNodeId, setSelectedSampleNodeId] = useState<string | null>(null);
  /** 整列時にのみ動的生成される、兄弟ノードを囲む視覚的な枠（ノード構成が変わったらクリアする） */
  const [groupNodes, setGroupNodes] = useState<Node[]>([]);
  /** 材料の並び替え用State */
  const [draggedMaterialIndex, setDraggedMaterialIndex] = useState<number | null>(null);

  // ---useDisclosure,useRef---

  /** 未保存の変更を破棄してよいかの確認ダイアログ */
  const { isOpen: isConfirmCloseOpen, onOpen: onOpenConfirmClose, onClose: onCloseConfirmClose } = useDisclosure();
  const confirmCloseCancelRef = useRef<HTMLButtonElement>(null);
  /** サンプルをベースに作成する際、既存の編集内容を上書きしてよいかの確認ダイアログ */
  const { isOpen: isConfirmLoadSampleOpen, onOpen: onOpenConfirmLoadSample, onClose: onCloseConfirmLoadSample } = useDisclosure();
  const confirmLoadSampleCancelRef = useRef<HTMLButtonElement>(null);
  /** アコーディオン開閉のstate */
  const { isOpen: isHelpOpen, onClose: onCloseHelp, onToggle: onToggleHelp } = useDisclosure();
  /** Excelダウンロード内容のリアルタイムプレビュー開閉 */
  const { isOpen: isPreviewOpen, onClose: onClosePreiew, onToggle: onTogglePreview } = useDisclosure();
  /** 新規Nodeを画面中央に配置するために、キャンバスのDOM要素とReactFlowインスタンスを保持する */
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);

  // ---useMemo---

  /**ノード/矢印の変更のたびにLevel（何次部品か）とProcess Code重複を再計算する。*/
  /** 表示（displayNodes）・ダウンロード可否判定・サイドパネルの警告表示すべてがこの2つを起点にする。*/
  const levelById = useMemo(() => computeLevels(nodes, edges), [nodes, edges]);
  const conflictById = useMemo(() => findProcessCodeConflicts(nodes), [nodes]);
  const selectedHasConflict = selectedNodeId ? !!conflictById[selectedNodeId] : false;
  /** 表示用にNodeを計算*/
  const displayNodes = useMemo(
    () => nodes.map((n) => ({
      ...n,
      data: { ...n.data, level: levelById[n.id] ?? 0, hasCodeConflict: !!conflictById[n.id] },
    })),
    [nodes, levelById, conflictById]
  );
  /** サンプルデータからLevelとProcess Codeを算出する。 */
  const sampleLevelById = useMemo(() => computeLevels(bomSampleNodes, bomSampleEdges), []);
  const sampleConflictById = useMemo(() => findProcessCodeConflicts(bomSampleNodes), []);
  const sampleDisplayNode = useMemo(
    () => bomSampleNodes.map((n) => ({
      ...n,
      selected: n.id === selectedSampleNodeId, //選択状態を明示的に設定。
      data: { ...n.data, level: sampleLevelById[n.id] ?? 0, hasCodeConflict: !!sampleConflictById[n.id] },
      // サンプルnodeの詳細を見る際、背景も一緒に動くことが多いので明示的にnodeを固定する。
      className: 'nopan',
    })),
    [selectedSampleNodeId, sampleLevelById, sampleConflictById]
  );
  /** サンプルのグループ枠（サンプルデータは不変のため、グループ枠は一度だけ計算すればよい） */
  const sampleGroupNodes = useMemo(() => buildGroupNodes(bomSampleNodes, bomSampleEdges), []);
  /** 選択タブによって表示するNodeを選択する。 */
  const selectedNode = activeTab === 'create'
    ? nodes.find((n) => n.id === selectedNodeId) ?? null
    : bomSampleNodes.find((n) => n.id === selectedSampleNodeId) ?? null;

  /**作成タブのExcel情報 */
  const previewRows = useMemo(() => buildBomExportAoa(nodes, edges), [nodes, edges]);
  /**サンプルタブのExcel情報 */
  const previewSampleRows = useMemo(() => buildBomExportAoa(bomSampleNodes, bomSampleEdges), []);
  /**選択タブに対応したExcel情報 */
  const currentPreviewRows = activeTab === 'create'
    ? previewRows
    : previewSampleRows;


  // ---------------------------------------------------------------------------------------------------------------
  // nodeの更新・追加・削除・整列
  // ---------------------------------------------------------------------------------------------------------------

  /** Nodeの更新 */
  function updateSelectedNodeField(key: keyof BomFlowNodeData, value: string) {
    if (!selectedNodeId) return;

    setNodes((nds) => nds.map((n) =>
      n.id === selectedNodeId ? { ...n, data: { ...n.data, [key]: value } } : n
    ));
  }

  /** 新規Nodeを追加 */
  function addNode() {
    // 工程の追加上限の判定（200）
    if (nodes.length >= MAX_NODES) {
      toast({
        title: t(`${bl}.message.maxNodesReachedTitle`),
        description: t(`${bl}.message.maxNodesReachedDesc`),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // ランダムなIdを採番
    const id = `bom-node-${crypto.randomUUID()}`;
    // 幅・高さはBomCreateFlowNode.tsx側の定数(BOM_NODE_WIDTH/BOM_NODE_HEIGHT)を正として参照する（buildGroupNodesと同値）
    const NODE_WIDTH = BOM_NODE_WIDTH, NODE_HEIGHT = BOM_NODE_HEIGHT;
    // キャンバスの画面中央を基準位置とする（ReactFlowインスタンス・DOM要素が未取得の場合は左上寄りの初期値にフォールバック）
    let basePosition = { x: BASE_POSITION_FALLBACK.x, y: BASE_POSITION_FALLBACK.y };
    const instance = reactFlowInstanceRef.current;
    const wrapperEl = canvasWrapperRef.current;
    if (instance && wrapperEl) {
      // サンプル画面から作成画面に移行時、新規でNodeを追加するとサンプル画面のズーム率をそのまま使用してしまうためここで明示的に設定する。
      if (nodes.length === 0) {
        const wrapperRect = wrapperEl.getBoundingClientRect();
        const initialZoom = 2;
        const newViewport = { x: wrapperRect.width / 2, y: wrapperRect.height / 2, zoom: initialZoom, };
        instance.setViewport(newViewport);
        setCreateViewport(newViewport);
        basePosition = { x: -NODE_WIDTH / 2, y: -NODE_HEIGHT / 2 };
      } else {
        const bounds = wrapperEl.getBoundingClientRect();
        const centerScreen = { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 };
        const flowPos = instance.screenToFlowPosition(centerScreen);
        // Node自体の中心が画面中央に来るよう、Node幅・高さの半分をオフセット
        basePosition = { x: flowPos.x - NODE_WIDTH / 2, y: flowPos.y - NODE_HEIGHT / 2 };
      }
    }
    // 新規Nodeのステータスを設定して追加（連続追加時に完全重複しないよう、既存件数に応じて少しずらす）
    setNodes((nds) => {
      const offset = (nds.length % 5) * 24;
      return [
        ...nds,
        {
          id,
          type: BOM_NODE_TYPE.PROCESS,
          position: { x: basePosition.x + offset, y: basePosition.y + offset },
          data: { modelNo: '', processCode: '', itemNo: '', itemName: t(`${bl}.field.newProcessDefaultName`), materials: [] },
        },
      ];
    });
    // 作成したidを選択
    setSelectedNodeId(id);
    setGroupNodes([]);
  }

  /** nodeの削除 */
  function deleteSelectedNode() {
    if (!selectedNodeId) return;
    // 選択しているNodeId以外を取得
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
    setGroupNodes([]);
  }

  /** Nodeの整列 */
  function handleLayoutClick() {
    const maxLevel = Math.max(0, ...nodes.map((n) => levelById[n.id] ?? 0));
    const byLevel: Record<number, BomFlowNode[]> = {};
    nodes.forEach((n) => {
      const lv = levelById[n.id] ?? 0;
      (byLevel[lv] ??= []).push(n);
    });
    // ROW_HEIGHT - NODE_HEIGHT(buildGroupNodes内) は GROUP_MARGIN*2 より大きくすること。
    // 隣接ノードが別グループに属す場合、双方の枠がGROUP_MARGIN分ずつ張り出すため、
    // 余白がそれ未満だと別グループ同士の枠が重なってしまう。
    const COL_WIDTH = 280, ROW_HEIGHT = 130, MARGIN_X = 30, MARGIN_Y = 30;
    // 子(e.source)から見た代表親(e.target)を1つだけ記録する。
    // 複数の親を持つノード（DAGの分岐）がある場合は、最初に登録された辺の親を代表とする。
    const representativeParentOf: Record<string, string> = {};
    edges.forEach((e) => { representativeParentOf[e.source] ??= e.target; });
    // Level内での行番号を、親ごとのブロック単位でまとめて振り直す。
    // これにより同一親を持つ子ノードは必ず連続したrowに配置され、buildGroupNodesのmin/max矩形が
    // 他の親グループのノードを巻き込まなくなる。
    const rowById: Record<string, number> = {};
    Object.values(byLevel).forEach((levelNodes) => {
      // 親を持たないノード（Level0の最終組立自身等）は従来通りY座標のみでソートする。
      const parentless = levelNodes.filter((n) => !representativeParentOf[n.id]);
      const parented = levelNodes.filter((n) => representativeParentOf[n.id]);

      // 親IDごとにグループ化し、各グループ内はY座標でソートする。
      const groupByParentId: Record<string, BomFlowNode[]> = {};
      parented.forEach((n) => {
        const pid = representativeParentOf[n.id];
        (groupByParentId[pid] ??= []).push(n);
      });
      Object.values(groupByParentId).forEach((members) => members.sort((a, b) => a.position.y - b.position.y));

      // 親グループ同士の並び順は、グループ内最小Y座標（現状のドラッグ操作の意図をなるべく尊重するため）で決める。
      const orderedGroups = Object.values(groupByParentId).sort(
        (a, b) => Math.min(...a.map((n) => n.position.y)) - Math.min(...b.map((n) => n.position.y))
      );

      const ordered = [...parentless.sort((a, b) => a.position.y - b.position.y), ...orderedGroups.flat()];
      ordered.forEach((n, row) => { rowById[n.id] = row; });
    });
    const nextNodes = nodes.map((n) => {
      const lv = levelById[n.id] ?? 0;
      const col = maxLevel - lv;
      const row = rowById[n.id] ?? 0;
      return { ...n, position: { x: MARGIN_X + col * COL_WIDTH, y: MARGIN_Y + row * ROW_HEIGHT } };
    });
    setNodes(nextNodes);
    setGroupNodes(buildGroupNodes(nextNodes, edges));
    // `fitView`propは初回マウント時にしか効かないため、整列後の座標に合わせて手動でビューポートを合わせ直す。
    // これをしないと、直前まで見えていた新規追加Nodeが整列によるグリッド再配置で現在の表示範囲外に移動してしまう。
    // `fitBounds`はズーム倍率も対象矩形に合わせて変更してしまい、ノード数が少ない場合に過大なズームインを起こすため使わない。
    // 代わりに現在のズーム倍率を維持したまま、整列後のノード群の中心が画面中心に来るようパンのみ行う。
    const instance = reactFlowInstanceRef.current;
    const wrapperEl = canvasWrapperRef.current;
    if (instance && wrapperEl) {
      // width/heightはgetNodesBounds内部でnode.width/height（DOM計測後にのみ設定される内部値）を参照するため、
      // ステート上のNodeオブジェクトには存在せず0扱いになる。ここで明示的に固定サイズ定数を補って矩形を正しく計算する。
      // positionAbsoluteはReactFlowがドラッグ操作のたびに書き込む内部値で、nextNodesにも整列前の古い値が残ったままになっている。
      // getNodesBoundsはpositionAbsoluteが存在するとそちらを優先し新しいpositionを無視してしまうため、明示的に破棄する。
      const boundsNodes = nextNodes.map((n) => ({
        ...n,
        width: BOM_NODE_WIDTH,
        height: BOM_NODE_HEIGHT,
        positionAbsolute: undefined,
      }));
      const bounds = getNodesBounds(boundsNodes);
      const boundsCenter = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
      const { zoom } = instance.getViewport();
      const wrapperRect = wrapperEl.getBoundingClientRect();
      // 新しいvierPortの設定
      const newViewPort = {
        x: wrapperRect.width / 2 - boundsCenter.x * zoom,
        y: wrapperRect.height / 2 - boundsCenter.y * zoom,
        zoom,
      };
      // 画面中心(wrapperRectの中心)がboundsCenter(flow座標)を映すように、ビューポート原点(x, y)を逆算する
      instance.setViewport(newViewPort);
      setCreateViewport(newViewPort);
    }
  }

  // ---------------------------------------------------------------------------------------------------------------
  // キャンバス状態管理・ノード/エッジ相互作用ハンドラー
  // ---------------------------------------------------------------------------------------------------------------

  /** 矢印（辺）をクリックしたら接続解除する。整列済みのグループ枠は構成が崩れるため一旦クリアする。 */
  function handleEdgeClick(_: React.MouseEvent, edge: BomFlowEdge) {
    setEdges((eds) => eds.filter((e) => e.id !== edge.id))
    setGroupNodes([]);
  }

  const canvasNodes = activeTab === 'create' ? [...displayNodes, ...groupNodes] : [...sampleDisplayNode, ...sampleGroupNodes];
  const canvasEdges = activeTab === 'create' ? edges : bomSampleEdges;

  /**
   * 同じ親を持つ兄弟が2つ以上のグループだけを対象に、現在の座標から外接矩形を計算して枠Nodeを生成する。
   * 整列直後（handleLayoutClick）・一時保存ファイル読み込み直後（handleTempLoad）の両方から呼び出す共通処理。
   */
  function buildGroupNodes(targetNodes: BomFlowNode[], targetEdges: BomFlowEdge[]): Node[] {
    // 幅・高さはBomCreateFlowNode.tsx側の定数(BOM_NODE_WIDTH/BOM_NODE_HEIGHT)を正として参照する（不一致だと枠の上下マージンがずれる）
    const NODE_WIDTH = BOM_NODE_WIDTH, NODE_HEIGHT = BOM_NODE_HEIGHT, GROUP_MARGIN = 16;
    const childrenOfParent: Record<string, string[]> = {};
    targetEdges.forEach((e) => { (childrenOfParent[e.target] ??= []).push(e.source); });
    const nodeById = Object.fromEntries(targetNodes.map((n) => [n.id, n]));
    return Object.entries(childrenOfParent)
      .filter(([, childIds]) => childIds.length >= 2)
      .map(([parentId, childIds]) => {
        const members = childIds.map((id) => nodeById[id]).filter(Boolean);
        const minX = Math.min(...members.map((m) => m.position.x));
        const minY = Math.min(...members.map((m) => m.position.y));
        const maxY = Math.max(...members.map((m) => m.position.y));
        const groupWidth = NODE_WIDTH + GROUP_MARGIN * 2;
        const groupHeight = (maxY - minY) + NODE_HEIGHT + GROUP_MARGIN * 2;
        return {
          id: `bom-group-${parentId}`,
          type: BOM_NODE_TYPE.GROUP,
          position: { x: minX - GROUP_MARGIN, y: minY - GROUP_MARGIN },
          width: groupWidth,
          height: groupHeight,
          style: { width: groupWidth, height: groupHeight },
          draggable: false,
          selectable: false,
          zIndex: -1,
          data: {},
        };
      });
  }

  /**
   * ノード同士を矢印で接続する時のハンドラ。
   * まず「もし繋いだら」を想定した辺の集合（tentativeEdges）を作り、
   * 実際にsetEdgesする前に循環（親子関係のループ）が発生しないかをチェックする。
   * 循環になる接続は許可せず、setEdgesを呼ばずに中断（＝GUI上は接続が反映されない）。
   */
  function handleConnect(connection: Connection) {
    const edgesWithoutExistingSource = edges.filter((e) => e.source !== connection.source);
    const tentativeEdges = addEdge(connection, edgesWithoutExistingSource);
    const cycle = detectCycle(nodes, tentativeEdges);
    if (cycle) {
      toast({
        title: t(`${bl}.message.cycleDetected`),
        description: t(`${bl}.message.cycleDetectedDescription`, { count: cycle.length }),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return; // setEdges を呼ばない = 接続をキャンセル
    }
    setEdges(tentativeEdges);
    setGroupNodes([]);
  }

  // ---------------------------------------------------------------------------------------------------------------
  //  材料の更新・追加・削除
  // ---------------------------------------------------------------------------------------------------------------


  /** 材料の追加 */
  function addMaterial() {
    if (!selectedNodeId) return;

    // 材料登録上限の判定（30件）
    const targetNode = nodes.find((n) => n.id === selectedNodeId);
    if (targetNode && targetNode.data.materials.length >= MAX_MATERIALS) {
      toast({
        title: t(`${bl}.message.maxMaterialsReachedTitle`),
        description: t(`${bl}.message.maxMaterialsReachedDesc`),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const material: BomMaterial = { id: `mat-${crypto.randomUUID()}`, itemNo: '', itemName: '' };
    setNodes((nds) => nds.map((n) =>
      n.id === selectedNodeId
        ? { ...n, data: { ...n.data, materials: [...n.data.materials, material] } } : n
    ));
  };

  /** 材料の更新 */
  function updateMaterialField(materialId: string, key: 'itemNo' | 'itemName', value: string) {
    if (!selectedNodeId) return;
    setNodes((nds) => nds.map((n) =>
      n.id === selectedNodeId
        ? { ...n, data: { ...n.data, materials: n.data.materials.map((m) => m.id === materialId ? { ...m, [key]: value } : m) } }
        : n
    ));
  }

  /** 材料の削除 */
  function deleteMaterial(materialId: string) {
    if (!selectedNodeId) return;
    setNodes((nds) => nds.map((n) =>
      n.id === selectedNodeId ? { ...n, data: { ...n.data, materials: n.data.materials.filter((m) => m.id !== materialId) } } : n
    ));
  }

  // ---------------------------------------------------------------------------------------------------------------
  // 材料の並び替え用ハンドラー
  // ---------------------------------------------------------------------------------------------------------------

  // ドラッグ操作の基本イベント
  const handleMaterialDragStart = (index: number) => setDraggedMaterialIndex(index);
  const handleMaterialDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleMaterialDragEnd = () => setDraggedMaterialIndex(null);

  /**
   * 材料のドラッグ＆ドロップ完了時（ドロップした瞬間）の処理
   * @param targetIndex ドロップされた位置のインデックス
   */
  const handleMaterialDrop = (targetIndex: number) => {
    // 無効な操作（ドラッグしていない、同じ場所に落とした、工程が未選択）の場合は何もしない
    if (draggedMaterialIndex === null || draggedMaterialIndex === targetIndex || !selectedNodeId) return;

    setNodes((nds) => nds.map((n) => {
      // 選択中の工程（ノード）以外はそのまま返す
      if (n.id !== selectedNodeId) return n;
      // 既存のstateを直接変更しないよう、materials配列のクローンを作成
      const newMaterials = [...n.data.materials];
      // ドラッグ元を削除し、ドロップ先に挿入（Scheduling.tsxのロジックを流用）
      const [removed] = newMaterials.splice(draggedMaterialIndex, 1);
      newMaterials.splice(targetIndex, 0, removed);
      return { ...n, data: { ...n.data, materials: newMaterials } };
    }));
    setDraggedMaterialIndex(null);
  };


  // ---------------------------------------------------------------------------------------------------------------
  //  Excelダウンロード
  // ---------------------------------------------------------------------------------------------------------------

  function handleDownloadClick() {
    // ダウンロード時の二重チェック（onConnect/リアルタイム入力チェックの保険。）
    const cycle = detectCycle(nodes, edges);
    if (cycle) {
      toast({ title: t(`${bl}.message.circularReferenceBlocksDownload`), status: 'error', duration: 4000, isClosable: true });
      return;
    }
    const conflicts = findProcessCodeConflicts(nodes);
    if (Object.keys(conflicts).length > 0) {
      toast({ title: t(`${bl}.message.processCodeDuplicateBlocksDownload`), status: 'error', duration: 4000, isClosable: true });
      return;
    }

    downloadBomExcel(nodes, edges);
    toast({ title: t(`${bl}.message.downloadCompleted`), status: 'success', duration: 3000, isClosable: true });
  }


  // ---------------------------------------------------------------------------------------------------------------
  //  一時保存・読み込み
  // ---------------------------------------------------------------------------------------------------------------

  function handleTempSave() {
    const snapshot = { nodes, edges };
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const json = JSON.stringify(
      { screenName: SCREEN_NAME, savedAt: new Date().toISOString(), data: snapshot },
      null,
      2
    );
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${SCREEN_NAME}_${dateStr}.data`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: t(`${bl}.message.tempSaved`), status: 'success', duration: 3000, isClosable: true });
  }

  function handleTempLoad() {
    // 隠しinput[type=file]を動的生成してクリックし、OSのファイル選択ダイアログを開く
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.data';
    input.onchange = (ev) => {
      const file = (ev.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result as string);
          // 別画面用の一時保存ファイルを誤って読み込まないよう、保存時に埋め込んだ画面名を照合する
          if (parsed.screenName !== SCREEN_NAME) {
            toast({ title: t(`${bl}.message.tempLoadScreenMismatch`), status: 'error', duration: 4000, isClosable: true });
            return;
          }
          // 妥当性チェックOKならnodes/edgesを丸ごと復元し、選択状態はリセットする
          const snapshot = parsed.data as { nodes: BomFlowNode[]; edges: BomFlowEdge[] };
          const loadedNodes = snapshot.nodes ?? [];
          const loadedEdges = snapshot.edges ?? [];
          setNodes(loadedNodes);
          setEdges(loadedEdges);
          setSelectedNodeId(null);
          // 整列時と同じロジックで、読み込んだ座標をもとにグループ枠を再生成する
          setGroupNodes(buildGroupNodes(loadedNodes, loadedEdges));
          toast({ title: t(`${bl}.message.tempLoaded`), status: 'success', duration: 3000, isClosable: true });
        } catch {
          // JSONパース失敗・形式不正など予期しないエラーはまとめてここで捕捉する
          toast({ title: t(`${bl}.message.tempLoadFailed`), status: 'error', duration: 4000, isClosable: true });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  //  ---------------------------------------------------------------------------------------------------------------
  //   サンプル処理関連
  //  ---------------------------------------------------------------------------------------------------------------

  /** サンプルデータを作成画面にセット */
  function applySampleAsTemplate() {
    // 参照共有によるサンプル定数の意図しない書き換えを避けるため、構造化クローンする。
    const clonedNodes = structuredClone(bomSampleNodes);
    const clonedEdges = structuredClone(bomSampleEdges);
    // サンプルデータをセット
    setNodes(clonedNodes);
    setEdges(clonedEdges);
    setSelectedNodeId(null);
    setGroupNodes(buildGroupNodes(clonedNodes, clonedEdges));
    // サンプル内容で作成タブのノード構成が丸ごと置き換わるため、以前の作成タブでの
    // viewport（ズーム倍率・位置）をそのまま復元すると内容とズレる。破棄してfitViewし直す。
    setCreateViewport(undefined);
    setActiveTab('create');
    setCreateViewport(reactFlowInstanceRef.current?.getViewport);
    onCloseConfirmLoadSample();
    toast({
      description: t(`${bl}.message.createSampleDesc`),
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }

  //  ---------------------------------------------------------------------------------------------------------------
  //   モーダルクローズ（未保存の変更がある場合は確認ダイアログを挟む。）
  //  ---------------------------------------------------------------------------------------------------------------

  function handleRequestClose() {
    // ノードが1つでも存在する（＝編集途中の可能性がある）場合は、
    // 即座に閉じずに確認ダイアログを開いて破棄してよいか確認する
    if (nodes.length > 0) {
      onOpenConfirmClose();
      return;
    }
    // 未編集（ノード0件）ならそのまま閉じてよい
    setActiveTab('create');
    onClosePreiew();
    onCloseHelp();
    onClose();
  }

  // 確認ダイアログで「閉じる」が選択された時：
  // Chakra UIのModalはunmountOnCloseを指定していないため、閉じてもこのコンポーネントのstateは
  // 保持されたままになる。次回オープン時に前回の続きが表示されてしまわないよう、
  // 「破棄に同意した」このタイミングで編集中のstateを明示的に初期化してからダイアログ・モーダルを閉じる。
  function handleConfirmClose() {
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setActiveTab('create');
    setGroupNodes([]);
    setCreateViewport(undefined);
    setSampleViewport(undefined);
    onClosePreiew();
    onCloseHelp();
    onCloseConfirmClose();
    onClose();
  }

  // ---------------------------------------------------------------------------------------------------------------
  // ビューポート制御
  // ---------------------------------------------------------------------------------------------------------------

  /**
 * 対象ノード群がキャンバス全体に収まるビューポート（x, y, zoom）を同期的に計算する。
 * `instance.fitView()`と同じ考え方だが、DOM計測（ResizeObserver）の完了を待たずに
 * 固定サイズ定数（BOM_NODE_WIDTH/BOM_NODE_HEIGHT）だけで計算するため、呼び出した瞬間に結果が確定する。
 * ※ MIN_ZOOM/MAX_ZOOMは下記<ReactFlow>にminZoom/maxZoomを明示指定していないため、
 *   ReactFlow側のデフォルト値(0.5 / 2)に合わせている。将来propを追加する場合はここも合わせて変更すること。
 */
  function computeFitViewport(targetNodes: Node[], wrapperEl: HTMLDivElement, padding = 0.2): Viewport | null {
    if (targetNodes.length === 0) return null;

    const boundsNodes = targetNodes.map((n) => ({
      ...n,
      width: BOM_NODE_WIDTH,
      height: BOM_NODE_HEIGHT,
      positionAbsolute: undefined,
    }));
    const bounds = getNodesBounds(boundsNodes);
    const boundsCenter = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
    const wrapperRect = wrapperEl.getBoundingClientRect();

    const MIN_ZOOM = 0.5;
    const MAX_ZOOM = 2;
    const rawZoom = Math.min(
      wrapperRect.width / bounds.width,
      wrapperRect.height / bounds.height,
    ) * (1 - padding);
    const zoom = Math.min(Math.max(rawZoom, MIN_ZOOM), MAX_ZOOM);

    return {
      x: wrapperRect.width / 2 - boundsCenter.x * zoom,
      y: wrapperRect.height / 2 - boundsCenter.y * zoom,
      zoom,
    };
  }

  /**タブ切り替え時のビューポート再調整 */
  function handleTabChange(nextTab: BomModalTab) {
    const savedViewport = nextTab === 'create' ? createViewport : sampleViewport;
    const instance = reactFlowInstanceRef.current;
    const wrapperEl = canvasWrapperRef.current;

    if (instance && wrapperEl) {
      const nextTargetNodes = nextTab === 'create'
        ? [...displayNodes, ...groupNodes]
        : [...sampleDisplayNode, ...sampleGroupNodes];
      const viewport = savedViewport ?? computeFitViewport(nextTargetNodes, wrapperEl);
      if (viewport) {
        instance.setViewport(viewport);
      }
    }
    setActiveTab(nextTab);
  }

  /** タブごとのviewportを記憶する（次回そのタブを開いたときに同じ表示状態へ復元するため） */
  function handleMoveEnd(_: unknown, viewport: Viewport) {
    if (activeTab === 'create') {
      setCreateViewport(viewport);
    } else {
      setSampleViewport(viewport);
    }
  }

  // ---------------------------------------------------------------------------------------------------------------
  // キー操作関連
  // ---------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    /** キー操作 */
    function handleKeyDown(e: KeyboardEvent) {
      // input/textarea編集中はキー操作を横取りしない
      const target = e.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea') return;

      // 連打制御
      if (tagName === 'button' && (e.key === 'Enter' || e.key === ' ') && e.repeat) {
        e.preventDefault();
        return;
      }

      // daleteキー操作時、既存の削除処理を呼ぶ。
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (activeTab !== 'create') return;
        deleteSelectedNode();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeId, activeTab]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleRequestClose} closeOnOverlayClick={false} isCentered>
        <ModalOverlay />
        <ModalContent maxW="1800px" h="90vh" maxH="90vh" overflow="hidden">
          <ModalHeader display="flex" alignItems="center" gap={2}>
            {t(`${bl}.title`)}
            <HelpIcon width="auto">
              {t(`${bl}.help.processLevelDeterminationHelp`)}
              {"\n\n"}
              {t(`${bl}.help.processModelNoHelp`)}
              {"\n\n"}
              {t(`${bl}.help.processCodeUniquenessHelp`)}
              {"\n\n"}
              {t(`${bl}.help.processMaterialHelp`)}
              {"\n\n"}
              {t(`${bl}.help.processNonSalesPartHelp`)}
            </HelpIcon>
          </ModalHeader>
          <ModalCloseButton />
          <Tabs
            index={activeTab === 'create' ? 0 : 1}
            onChange={(index) => {
              handleTabChange(index === 0 ? 'create' : 'sample');
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
            }
            }
            top={0} left={5} zIndex={11} size="md"
          >
            <TabList>
              <Tab>{t(`${bl}.tab.create`)}</Tab>
              <Tab>{t(`${bl}.tab.sample`)}</Tab>
            </TabList>
          </Tabs>
          <ModalBody display="flex" flexDirection="column" overflow="hidden" flex="1" minH={0} pb={4}>
            <HStack align="stretch" flex="1" minH={0} spacing={4}>
              {/* キャンバス */}
              <Box ref={canvasWrapperRef} flex="1" h="100%" position="relative" border="1px solid #e2e8f0" borderRadius="md"
                bg={activeTab === 'sample' ? 'gray.100' : 'white'}
              >

                <HStack position="absolute" top={2} left={2} zIndex={10} spacing={2}>
                  {/* サンプルベース作成ボタン */}
                  {activeTab === 'create' && (
                    <>
                      {/* 工程を追加ボタン */}
                      <Button size="sm" colorScheme="teal" onClick={addNode}>
                        {t(`${bl}.button.addProcess`)}
                      </Button>
                      {/* 整列ボタン */}
                      <Button size="sm" variant="outline" bg="white" onClick={handleLayoutClick}>
                        {t(`${bl}.button.layout`)}
                      </Button>
                      {/* 使い方ボタン */}
                      <Button size="sm" variant="outline" bg="white" onClick={onToggleHelp}>
                        {t(`${bl}.button.help`)}
                      </Button>
                    </>
                  )}
                  {/* サンプルベース作成ボタン */}
                  {activeTab === 'sample' && (
                    <Button size="sm" colorScheme="teal" onClick={onOpenConfirmLoadSample}>
                      {t(`${bl}.button.useSampleAsTemplate`)}
                    </Button>
                  )}
                </HStack>
                <Box h="100%">
                  <ReactFlow
                    deleteKeyCode={null}
                    onInit={(instance) => { reactFlowInstanceRef.current = instance; }}
                    nodes={canvasNodes}
                    edges={canvasEdges}
                    onNodesChange={(activeTab === 'create' ? onNodesChange : () => { })}
                    onEdgesChange={activeTab === 'create' ? onEdgesChange : undefined}
                    onConnect={activeTab === 'create' ? handleConnect : undefined}
                    onNodeClick={(_, node) => activeTab === 'create' ? setSelectedNodeId(node.id) : setSelectedSampleNodeId(node.id)}
                    onNodeDragStart={activeTab === 'create' ? (_, node) => setSelectedNodeId(node.id) : undefined}
                    onPaneClick={() => activeTab === 'create' ? setSelectedNodeId(null) : setSelectedSampleNodeId(null)}
                    onEdgeClick={activeTab === 'create' ? handleEdgeClick : undefined}    // 矢印クリックで解除
                    onMoveEnd={handleMoveEnd}
                    nodeTypes={nodeTypes}
                    connectionLineType={ConnectionLineType.SmoothStep}
                    nodesDraggable={activeTab === 'create'}
                    nodesConnectable={activeTab === 'create'}
                    elementsSelectable
                    fitView
                    // 矢印の大きさ設定
                    defaultEdgeOptions={{
                      style: { strokeWidth: 3 },
                      markerEnd: { type: MarkerType.ArrowClosed, width: 10, height: 10 },
                    }}
                  />
                </Box>
                {/* 使い方パネル */}
                {isHelpOpen && (
                  <Box
                    position="absolute"
                    top={0}
                    right={0}
                    bottom={0}
                    w="320px"
                    bg="white"
                    borderLeft="1px solid #e2e8f0"
                    boxShadow="-4px 0 12px -8px rgba(0,0,0,0.25)"
                    zIndex={20}
                    overflowY="auto"
                    p={3}
                  >
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="bold" fontSize="sm">{t(`${bl}.help.title`)}</Text>
                      <IconButton
                        aria-label={t('common.button.close')}
                        icon={<CloseIcon />}
                        size="xs"
                        variant="ghost"
                        onClick={onToggleHelp}
                      />
                    </HStack>
                    <Accordion allowToggle defaultIndex={[0]} fontSize="sm">
                      {HELP_SECTIONS.map((key) => (
                        <AccordionItem key={key}>
                          <AccordionButton>
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              {t(`${bl}.help.${key}.heading`)}
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel color="gray.600">
                            <Text mb={1}>{t(`${bl}.help.${key}.body`)}</Text>
                            {(t(`${bl}.help.${key}.notes`, { returnObjects: true, defaultValue: [] }) as string[])
                              .map((note, i) => (
                                <Text key={i} fontSize="xs" color="gray.500">※{note}</Text>
                              ))}
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </Box>
                )}
              </Box>

              {/* サイドパネル：ノード未選択時はヒント文言のみ、選択時は工程情報＋材料一覧の編集フォームを表示する */}
              <Box w="320px" flexShrink={0} h="100%" overflowY="auto" borderLeft="1px solid #e2e8f0" pl={4}>
                {selectedNode ? (
                  <VStack align="stretch" spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">{t(`${bl}.label.modelNo`)}</FormLabel>
                      {/* PartNo */}
                      <Input
                        size="sm"
                        value={selectedNode.data.modelNo}
                        onChange={(e) => updateSelectedNodeField('modelNo', e.target.value)}
                        isReadOnly={activeTab === 'sample'}
                        bg={activeTab === 'sample' ? "gray.100" : "white"}
                      />
                    </FormControl>
                    <FormControl isInvalid={selectedHasConflict}>
                      <FormLabel fontSize="sm">
                        <Flex align="center" gap={2}>
                          {t(`${bl}.label.processCode`)}
                          <HelpIcon size="18">{t('features.InvestmentScreening.BomInfoList.tooltip.processCode')}</HelpIcon>
                        </Flex>
                      </FormLabel>
                      {/* ProcessCode */}
                      <Input
                        size="sm"
                        value={selectedNode.data.processCode}
                        onChange={(e) => updateSelectedNodeField('processCode', e.target.value)}
                        isReadOnly={activeTab === 'sample'}
                        bg={activeTab === 'sample' ? "gray.100" : "white"}
                      />
                      {selectedHasConflict && (
                        <FormErrorMessage>{t(`${bl}.message.processCodeDuplicate`)}</FormErrorMessage>
                      )}
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        <Flex align="center" gap={2}>
                          {t(`${bl}.label.itemNo`)}
                          <HelpIcon size="18">{t('features.InvestmentScreening.BomInfoList.tooltip.itemNo')}</HelpIcon>
                        </Flex>
                      </FormLabel>
                      {/* itemNo */}
                      <Input
                        size="sm"
                        value={selectedNode.data.itemNo}
                        onChange={(e) => updateSelectedNodeField('itemNo', e.target.value)}
                        isReadOnly={activeTab === 'sample'}
                        bg={activeTab === 'sample' ? "gray.100" : "white"}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">{t(`${bl}.label.itemName`)}</FormLabel>
                      {/* itemName */}
                      <Input
                        size="sm"
                        value={selectedNode.data.itemName}
                        onChange={(e) => updateSelectedNodeField('itemName', e.target.value)}
                        isReadOnly={activeTab === 'sample'}
                        bg={activeTab === 'sample' ? "gray.100" : "white"}
                      />
                    </FormControl>

                    <Button size="sm" colorScheme="red" variant="outline" onClick={deleteSelectedNode} isDisabled={activeTab === 'sample'}>
                      {t(`${bl}.button.deleteProcess`)}
                    </Button>

                    <Divider />

                    <Text fontWeight="bold" fontSize="sm">{t(`${bl}.label.materials`)}</Text>
                    {selectedNode.data.materials.map((m, index) => (
                      <HStack
                        key={m.id}
                        spacing={2}
                        draggable={activeTab === 'create'}
                        onDragStart={() => handleMaterialDragStart(index)}
                        onDragOver={handleMaterialDragOver}
                        onDrop={() => handleMaterialDrop(index)}
                        onDragEnd={handleMaterialDragEnd}
                        bg={draggedMaterialIndex === index ? "blue.50" : "transparent"}
                        p={1}
                        borderRadius="md"
                        cursor="grab"
                        borderWidth="1px"
                        borderColor="gray.200"
                        _hover={{ borderColor: "blue.300" }}
                      >
                        <DragHandleIcon cursor="grab" color="gray.400" ml={1} />
                        {/* 材料 itemNo */}
                        <Input
                          size="sm"
                          placeholder={t(`${bl}.label.itemNo`)}
                          value={m.itemNo}
                          onChange={(e) => updateMaterialField(m.id, 'itemNo', e.target.value)}
                          isReadOnly={activeTab === 'sample'}
                          bg={activeTab === 'sample' ? "gray.100" : "white"}
                        />
                        {/* 材料 itemName */}
                        <Input
                          size="sm"
                          placeholder={t(`${bl}.label.itemName`)}
                          value={m.itemName}
                          onChange={(e) => updateMaterialField(m.id, 'itemName', e.target.value)}
                          isReadOnly={activeTab === 'sample'}
                          bg={activeTab === 'sample' ? "gray.100" : "white"}
                        />
                        {/* ×ボタン */}
                        <IconButton
                          aria-label={t(`${bl}.button.deleteMaterial`)}
                          icon={<CloseIcon />}
                          size="xs"
                          onClick={() => deleteMaterial(m.id)}
                          isDisabled={activeTab === 'sample'}
                        />
                      </HStack>
                    ))}
                    {/* 材料追加ボタン */}
                    <Button size="sm" leftIcon={<AddIcon />} variant="ghost" onClick={addMaterial} isDisabled={activeTab === 'sample'}>
                      {t(`${bl}.button.addMaterial`)}
                    </Button>
                  </VStack>
                ) : (
                  <Text color="gray.500" fontSize="sm">{t(`${bl}.label.selectNodeHint`)}</Text>
                )}
              </Box>
            </HStack>

            {/* Excelダウンロード内容のリアルタイムプレビュー */}
            <Box mt={3} flexShrink={0}>
              <Button size="sm" variant="link" onClick={onTogglePreview}>
                {isPreviewOpen ? t(`${bl}.button.hidePreview`) : t(`${bl}.button.showPreview`)}
              </Button>
              <Collapse in={isPreviewOpen} animateOpacity>
                <Box maxH="200px" overflowY="auto" border="1px solid #e2e8f0" borderRadius="md" mt={2}>
                  <Table size="sm">
                    <Thead position="sticky" top={0} bg="white">
                      <Tr>
                        {currentPreviewRows[0].map((h, i) => <Th key={i}>{h}</Th>)}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {currentPreviewRows.slice(1).map((row, i) => (
                        <Tr key={i}>
                          {row.map((cell, j) => <Td key={j}>{cell}</Td>)}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Collapse>
            </Box>
          </ModalBody>
          {/* フッター */}
          <ModalFooter>
            {/* 一時保存ボタン */}
            <Button variant="outline" onClick={handleTempSave} mr={2} isDisabled={activeTab !== 'create'}>
              {t(`${bl}.button.tempSave`)}
            </Button>
            {/* 読み込みボタン */}
            <Button variant="outline" onClick={handleTempLoad} mr={2} isDisabled={activeTab !== 'create'}>
              {t(`${bl}.button.tempLoad`)}
            </Button>
            {/* Excelダウンロードボタン */}
            <Button colorScheme="teal" onClick={handleDownloadClick} mr={2} isDisabled={activeTab !== 'create'}>
              {t(`${bl}.button.downloadExcel`)}
            </Button>
            {/* 閉じるボタン */}
            <Button variant="ghost" onClick={handleRequestClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 未保存の変更を破棄する確認ダイアログ */}
      <AlertDialog
        isOpen={isConfirmLoadSampleOpen}
        leastDestructiveRef={confirmLoadSampleCancelRef}
        onClose={onCloseConfirmLoadSample}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t(`${bl}.dialog.confirmLoadSample.title`)}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t(`${bl}.dialog.confirmLoadSample.body`)}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button colorScheme="blue" onClick={applySampleAsTemplate}>
                {t('common.button.execute')}
              </Button>
              <Button onClick={onCloseConfirmLoadSample} ml={3}>
                {t('common.button.cancel')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* 未保存の変更を破棄する確認ダイアログ */}
      <AlertDialog
        isOpen={isConfirmCloseOpen}
        leastDestructiveRef={confirmCloseCancelRef}
        onClose={onCloseConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t(`${bl}.dialog.confirmClose.title`)}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t(`${bl}.dialog.confirmClose.body`)}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={confirmCloseCancelRef} onClick={onCloseConfirmClose}>
                {t('common.button.cancel')}
              </Button>
              <Button colorScheme="red" onClick={handleConfirmClose} ml={3}>
                {t('common.button.close')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );

}
export default BomCreateModal;